import { Component, HostListener, OnInit, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { BasketService } from '../../services/basket.service';
import { BasketItem, OwnedVoucher, VoucherItem } from '../../models/pilates.model';
import { environment } from '../../../environments/environment';
import { DateTimeFormatService } from '../../services/datetimeformat.service';
import { LogService } from '../../services/log/log.service';

@Component({
  selector: 'pil-header',
  standalone: true,
 imports: [RouterLink, RouterLinkActive],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly basketService = inject(BasketService);
  readonly formatService = inject(DateTimeFormatService);
  readonly logService = inject(LogService);
  readonly router = inject(Router);

  /** Test and development builds carry the test logo, so they can't be mistaken for the live site. */
  readonly logoSrc = environment.test ? 'assets/images/logo-test.jpeg' : 'assets/images/logo.jpeg';

  isMenuOpen = false;
  isScrolled = false;
  isBasketOpen = false;
  isUserOpen = false;

  private basketCloseTimer?: ReturnType<typeof setTimeout>;
  private userCloseTimer?: ReturnType<typeof setTimeout>;

  get isLoggedIn(): boolean { return this.authService.isLoggedIn; }
  get basketCount(): number { return this.basketService.lineCount(); }
  get basketItems(): BasketItem[] { return this.basketService.basket()?.items ?? []; }
  get voucherItems(): VoucherItem[] { return this.basketService.basket()?.voucherItems ?? []; }
  get ownedVouchers(): OwnedVoucher[] { return this.basketService.basket()?.vouchers ?? []; }
  /** Anything actually being bought — owned cards alone are not a basket. */
  get hasBasketLines(): boolean { return !(this.basketService.basket()?.empty ?? true); }
  get basketTotal(): number { return this.basketService.basket()?.total ?? 0; }
  get isCheckoutPage(): boolean { return this.router.url === '/checkout'}
  get userName(): string { return this.authService.userName; }

  ngOnInit(): void {
    if (this.isLoggedIn) {
      this.basketService.load().subscribe({ error: () => {} });
    }
  }

  /** Opens the basket preview, cancelling any pending close from a previous leave. */
  openBasketPreview(): void {
    clearTimeout(this.basketCloseTimer);
    this.isBasketOpen = true;
  }

  /**
   * Closes the basket preview after a short delay, so moving the pointer from
   * the basket icon across the small gap onto the panel doesn't dismiss it.
   */
  closeBasketPreview(): void {
    clearTimeout(this.basketCloseTimer);
    this.basketCloseTimer = setTimeout(() => { this.isBasketOpen = false; }, 150);
  }

  /** Opens the user menu, cancelling any pending close from a previous leave. */
  openUserMenu(): void {
    clearTimeout(this.userCloseTimer);
    this.isUserOpen = true;
  }

  /**
   * Mirrors closeBasketPreview: closes the user menu after a short delay so the
   * pointer can cross the gap between the trigger and the panel without dismissing it.
   */
  closeUserMenu(): void {
    clearTimeout(this.userCloseTimer);
    this.userCloseTimer = setTimeout(() => { this.isUserOpen = false; }, 150);
  }

  public hasRole(role: string): boolean {
    return this.authService.hasRole(role);
  }

  openCustomerOverview() {
    this.router.navigate(['/overview']);
  }

  openAdmin() {
    this.router.navigate(['/admin']);
  }

  logout(): void {
    this.isUserOpen = false;
    this.authService.logout();
  }

  @HostListener('window:scroll')
  onScroll(): void {
    this.isScrolled = window.scrollY > 20;
  }
}
